import React from 'react';
import { 
  Lightbulb, 
  Key, 
  ArrowRight, 
  Users, 
  Clock,
  PlayCircle,
  ArrowRightCircle,
  Shield,
  Zap
} from 'lucide-react';

interface StrategyRendererProps {
  value: any;
  key: string;
}

export const renderValue = (value: any, key: string): React.ReactNode => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">Not specified</span>;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object') {
        value = parsed;
      }
    } catch (e) {
      // Not a JSON string, continue with normal string rendering
    }
  }

  if (Array.isArray(value)) {
    return (
      <ul className="list-none space-y-1">
        {value.map((item, index) => (
          <li key={index} className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>{String(item)}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    // Handle strategy objects
    if (key === 'offensive_strategy' || key === 'defensive_strategy') {
      const strategy = value as {
        overview?: string;
        keyPoints?: string[];
        playRecommendations?: Array<{
          playType: string;
          formation: string;
          personnel: string;
          situation: string;
          notes: string;
        }>;
        gameSituation?: {
          redZone: string;
          shortYardage: string;
          twoMinute: string;
        };
        alignmentRecommendations?: Array<{
          formation: string;
          front: string;
          coverage: string;
          situation: string;
        }>;
        blitzPackages?: Array<{
          type: string;
          situation: string;
          effectiveness: string;
        }>;
      };

      const isOffense = key === 'offensive_strategy';
      const bgColor = isOffense ? 'bg-blue-50' : 'bg-red-50';
      const borderColor = isOffense ? 'border-blue-200' : 'border-red-200';
      const textColor = isOffense ? 'text-blue-700' : 'text-red-700';
      const iconColor = isOffense ? 'text-blue-500' : 'text-red-500';

      return (
        <div className="space-y-3">
          {strategy.overview && (
            <div className={`${bgColor} ${borderColor} border rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className={`w-5 h-5 ${iconColor}`} />
                <h4 className="font-semibold">Overview</h4>
              </div>
              <p className="text-gray-700 text-sm">{strategy.overview}</p>
            </div>
          )}
          
          {strategy.keyPoints && strategy.keyPoints.length > 0 && (
            <div className={`${bgColor} ${borderColor} border rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <Key className={`w-5 h-5 ${iconColor}`} />
                <h4 className="font-semibold">Key Points</h4>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {strategy.keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-2 bg-white/50 rounded p-2">
                    <ArrowRightCircle className={`w-4 h-4 mt-1 ${iconColor}`} />
                    <span className="text-sm text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {strategy.playRecommendations && strategy.playRecommendations.length > 0 && (
            <div className={`${bgColor} ${borderColor} border rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <PlayCircle className={`w-5 h-5 ${iconColor}`} />
                <h4 className="font-semibold">Play Recommendations</h4>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {strategy.playRecommendations.map((play, index) => (
                  <div key={index} className="bg-white/50 rounded p-2">
                    <div className="font-medium text-sm">{play.playType}</div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div>Formation: {play.formation}</div>
                      <div>Personnel: {play.personnel}</div>
                      {play.situation && play.situation !== 'General' && (
                        <div>Situation: {play.situation}</div>
                      )}
                    </div>
                    {play.notes && (
                      <div className="mt-1 pt-1 border-t border-gray-200">
                        <div className="text-xs text-gray-600">{play.notes}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Defensive Specific Sections */}
          {!isOffense && strategy.alignmentRecommendations && strategy.alignmentRecommendations.length > 0 && (
            <div className={`${bgColor} ${borderColor} border rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <Shield className={`w-5 h-5 ${iconColor}`} />
                <h4 className="font-semibold">Alignment Recommendations</h4>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {strategy.alignmentRecommendations.map((alignment, index) => (
                  <div key={index} className="bg-white/50 rounded p-2">
                    <div className="font-medium text-sm">{alignment.formation}</div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div>Front: {alignment.front}</div>
                      <div>Coverage: {alignment.coverage}</div>
                      <div>Situation: {alignment.situation}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isOffense && strategy.blitzPackages && strategy.blitzPackages.length > 0 && (
            <div className={`${bgColor} ${borderColor} border rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <Zap className={`w-5 h-5 ${iconColor}`} />
                <h4 className="font-semibold">Blitz Packages</h4>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {strategy.blitzPackages.map((blitz, index) => (
                  <div key={index} className="bg-white/50 rounded p-2">
                    <div className="font-medium text-sm">{blitz.type}</div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div>Situation: {blitz.situation}</div>
                      <div>Effectiveness: {blitz.effectiveness}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {strategy.gameSituation && (
            <div className={`${bgColor} ${borderColor} border rounded-lg p-3`}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className={`w-5 h-5 ${iconColor}`} />
                <h4 className="font-semibold">Game Situations</h4>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(strategy.gameSituation).map(([situation, details]) => (
                  <div key={situation} className="bg-white/50 rounded p-2">
                    <div className="font-medium text-sm">
                      {situation
                        .replace(/([A-Z])/g, ' $1')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')}
                    </div>
                    <div className="text-xs text-gray-600">{details}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <ul className="list-none space-y-1">
        {Object.entries(value).map(([k, v]) => (
          <li key={k} className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <div>
              <span className="font-medium text-sm">{k.replace(/([A-Z])/g, ' $1').trim()}:</span>
              <span className="ml-1 text-sm">{renderValue(v, k)}</span>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return <span className="text-sm">{String(value)}</span>;
}; 